import { hFetch } from '@/lib/hfetch';
import type { ContactEventEntity } from '@/api/entity_types/ContactEvent';
import type { EventRecordingEntity } from '@/api/entity_types/EventRecording';
import type {
  PipelineReviewEntity,
  PipelineReviewResultEntity,
  PipelineReviewStage,
  PipelineReviewStatus,
} from '@/api/entity_types/PipelineReview';

export type CreatePipelineReviewParams = {
  private_notes: string;
  score: number | null;
  contact_event_id?: number;
  files?: File[];
  answers: { question_id: number; answer: string }[];
};
export const createPipelineReview = async (contactId: number, params: CreatePipelineReviewParams) => {
  const formData = new FormData();
  formData.append('private_notes', params.private_notes);
  formData.append('score', String(params.score));
  if (params.contact_event_id) {
    formData.append('contact_event_id', String(params.contact_event_id));
  }
  for (const answer of params.answers) {
    formData.append('answers[][question_id]', String(answer.question_id));
    formData.append('answers[][answer]', answer.answer);
  }
  for (const file of params.files ?? []) {
    formData.append('files[]', file, file.name);
  }

  return await hFetch<PipelineReviewEntity, FormData>({
    url: `/api/contacts/${contactId}/pipeline_reviews`,
    method: 'POST',
    payload: formData,
  });
};

export type PipelineReviewListResponse = PipelineReviewShowRecord & {
  contact_event: ContactEventEntity | null;
  result: PipelineReviewResultEntity | null;
};
export const listPipelineReviews = async (contactId: number) => {
  return await hFetch<PipelineReviewListResponse[]>({
    url: `/api/contacts/${contactId}/pipeline_reviews`,
  });
};

export type PipelineReviewShowRecord = PipelineReviewEntity & {
  file_objects: { id: number; name: string; url: string }[] | null;
  status: PipelineReviewStatus;
};

export type PipelineReviewShowResponse = {
  pipeline_review: PipelineReviewShowRecord & { result: PipelineReviewResultEntity | null };
  contact_event: (ContactEventEntity & { event_recording: EventRecordingEntity | null }) | null;
};
export const getPipelineReview = async (contactId: number, pipelineReviewId: number) => {
  return await hFetch<PipelineReviewShowResponse>({
    url: `/api/contacts/${contactId}/pipeline_reviews/${pipelineReviewId}`,
  });
};

export type UpdatePipelineReviewParams = {
  private_notes: string | null;
  score: number;
};
export const updatePipelineReview = async (
  contactId: number,
  pipelineReviewId: number,
  params: UpdatePipelineReviewParams
) => {
  return await hFetch<PipelineReviewEntity & { status: PipelineReviewStatus }, UpdatePipelineReviewParams>({
    url: `/api/contacts/${contactId}/pipeline_reviews/${pipelineReviewId}`,
    method: 'PATCH',
    payload: params,
  });
};

export type ContactEventResponse = (ContactEventEntity & { event_recording: EventRecordingEntity | null }) | null;

export const getContactEvent = async (contactId: number) => {
  return await hFetch<ContactEventResponse>({
    url: `/api/contacts/${contactId}/pipeline_reviews/contact_event`,
  });
};

export type AllPipelineReviewsRecord = {
  id: number;
  created_at: string;
  stage: PipelineReviewStage;
  status: PipelineReviewStatus;
  review_type: 'sync' | 'async' | null;
  contact_event_id: number | null;
  teammate_contact: { id: number; name: string; email: string | null } | null;
  pipeline_reviewer: { id: number; name: string } | null;
  result: { id: number; score: number; private_notes: string } | null;
};

export type AllPipelineReviewsParams = {
  status?: PipelineReviewStatus;
  review_type?: string;
  reviewer_id?: number | string;
  contact_id?: number | string;
  search?: string;
  sort?: string;
  order?: 'asc' | 'desc';
  page?: number | string;
};

export type AllPipelineReviewsResponse = {
  records: AllPipelineReviewsRecord[];
  total: number;
  start_entry: number;
  end_entry: number;
};

export const listAllPipelineReviews = async (params: AllPipelineReviewsParams = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    search.append(key, String(value));
  });
  const queryString = search.toString();
  return await hFetch<AllPipelineReviewsResponse>({
    url: `/api/pipeline_reviews${queryString ? `?${queryString}` : ''}`,
  });
};
